import { useEffect, useRef } from "react";

const COLORS = ["212,175,55", "230,197,148", "243,229,171", "201,160,90", "255,240,200"];

// Desert sand blowing across the hero: a strong gust on load that settles into a slow drift.
// The pointer (mouse / touch) acts as a local gust pushing grains away.
export const Sand = ({ className = "" }) => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const ctx = canvas.getContext("2d");
    let w = 0, h = 0, dpr = 1, raf = 0, visible = true, last = performance.now();
    const start = last;
    const mobile = window.innerWidth < 768;
    const N = mobile ? 80 : 190;
    const grains = [];
    // pointer state: position, velocity, and "energy" that decays when idle
    const ptr = { x: -9999, y: -9999, vx: 0, vy: 0, on: false, t: 0 };
    const R = mobile ? 130 : 210;

    const spawn = (g, fresh) => {
      g.x = fresh ? Math.random() * w : -20 - Math.random() * w * 0.3;
      g.y = h * 0.25 + Math.random() * h * 0.8;
      g.r = 0.3 + Math.random() * 0.9;
      g.v = 0.6 + Math.random() * 1.2;
      g.vx = 0; g.vy = 0;
      g.wob = Math.random() * Math.PI * 2;
      g.ws = 0.5 + Math.random() * 1.5;
      g.a = 0.12 + Math.random() * 0.45;
      g.c = COLORS[(Math.random() * COLORS.length) | 0];
      g.life = 0;
      g.ttl = 4 + Math.random() * 8;
      return g;
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth; h = canvas.clientHeight;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    for (let i = 0; i < N; i++) grains.push(spawn({}, true));

    const setPointer = (cx, cy) => {
      const r = canvas.getBoundingClientRect();
      const x = cx - r.left, y = cy - r.top;
      const t = performance.now();
      if (ptr.on) {
        const dt = Math.max((t - ptr.t) / 1000, 0.008);
        ptr.vx = ptr.vx * 0.5 + ((x - ptr.x) / dt) * 0.5;
        ptr.vy = ptr.vy * 0.5 + ((y - ptr.y) / dt) * 0.5;
      }
      ptr.x = x; ptr.y = y; ptr.t = t; ptr.on = true;
    };
    const onMouse = (e) => setPointer(e.clientX, e.clientY);
    const onTouch = (e) => { const t = e.touches[0]; if (t) setPointer(t.clientX, t.clientY); };
    const onLeave = () => { ptr.on = false; ptr.x = -9999; ptr.y = -9999; };

    const draw = (now) => {
      raf = requestAnimationFrame(draw);
      if (!visible) return;
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      const t = (now - start) / 1000;
      // gust: strong at load (first ~3.5s), then gentle breeze with slow periodic gusts
      const gust = 1 + 3.2 * Math.exp(-t / 1.6) + 0.6 * Math.max(0, Math.sin(t * 0.35));
      const wind = (mobile ? 110 : 170) * gust;
      // pointer gust fades when the cursor stops moving
      const speed = Math.hypot(ptr.vx, ptr.vy);
      const energy = ptr.on ? Math.min(1, 0.25 + speed / 1400) : 0;
      ptr.vx *= 0.9; ptr.vy *= 0.9;

      ctx.clearRect(0, 0, w, h);
      for (const g of grains) {
        g.life += dt;
        g.wob += g.ws * dt;
        if (energy > 0) {
          const dx = g.x - ptr.x, dy = g.y - ptr.y;
          const d = Math.hypot(dx, dy);
          if (d < R && d > 0.01) {
            const f = (1 - d / R) * (1 - d / R) * energy;
            // radial push away from the cursor + drag along its direction of motion
            g.vx += ((dx / d) * 1400 * f + ptr.vx * 1.2 * f) * dt;
            g.vy += ((dy / d) * 1400 * f + ptr.vy * 1.2 * f) * dt;
          }
        }
        g.vx *= 1 - 2.4 * dt; g.vy *= 1 - 2.4 * dt;
        g.x += (wind * g.v + g.vx) * dt;
        g.y += ((Math.sin(g.wob) * 18 - 6 * gust) * g.v + g.vy) * dt;
        if (g.x > w + 20 || g.x < -w * 0.35 || g.y < -10 || g.y > h + 10 || g.life > g.ttl) spawn(g, false);
        const fade = Math.min(1, g.life * 2, (g.ttl - g.life) * 1.5);
        const sx = wind * g.v + g.vx, sy = g.vy;
        const sp = Math.hypot(sx, sy) || 1;
        const len = Math.min(14, sp * 0.03 * g.r);
        ctx.strokeStyle = `rgba(${g.c},${g.a * fade})`;
        ctx.lineWidth = g.r;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(g.x, g.y);
        ctx.lineTo(g.x - (sx / sp) * len, g.y - (sy / sp) * len + len * 0.12);
        ctx.stroke();
      }
    };
    raf = requestAnimationFrame(draw);

    const host = canvas.parentElement;
    host.addEventListener("mousemove", onMouse, { passive: true });
    host.addEventListener("mouseleave", onLeave);
    host.addEventListener("touchstart", onTouch, { passive: true });
    host.addEventListener("touchmove", onTouch, { passive: true });
    host.addEventListener("touchend", onLeave);
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (visible) last = performance.now(); });
    io.observe(canvas);
    const onVis = () => { last = performance.now(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      host.removeEventListener("mousemove", onMouse);
      host.removeEventListener("mouseleave", onLeave);
      host.removeEventListener("touchstart", onTouch);
      host.removeEventListener("touchmove", onTouch);
      host.removeEventListener("touchend", onLeave);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return <canvas ref={ref} aria-hidden="true" className={`absolute inset-0 w-full h-full pointer-events-none ${className}`} data-testid="hero-sand-canvas" />;
};
